import { Editor } from '@monaco-editor/react';
import { FieldType } from 'bindings/forms/FieldType';
import * as monaco from 'monaco-editor';
import * as React from 'react';
import styled from 'styled-components';
import Add from '../../components_v2/add/Add';
import { getUserColor } from '../../components_v2/avatar/Data/Data';
import Dropdown from '../../components_v2/dropdown/Dropdown';
import DropdownColor, { DropdownIcons } from '../../components_v2/dropdown/DropdownColors';
import Input from '../../components_v2/input/Input';
import { PopupMode } from '../../components_v2/popup/model/Model';
import Popup from '../../components_v2/popup/Popup';
import { FieldGuidePopup, GuideKind, LabelWithGuide } from '../promotions/FormulaGuide';
import MapColorsPopup from '../../components_v2/colorScale/MapColorsPopup';
import { ColumnDisplayOptions, mapColorsKindOf } from '../map/colorScale';
import { Palette } from 'lucide-react';

// whether display options carry any colours
const hasColorOptions = (options: ColumnDisplayOptions | undefined): boolean => !!(options?.color_scale || options?.boolean_colors);
import PopupCreation, { ButtonStyle } from '../../components_v2/popup/PopupCreation';
import { getLocalStorage } from '../../helpers/localStorage';
import CloseIcon from '../../styles/custom-icons/close';
import {
	PrimaryColor,
	BorderColor,
	DarkGreySidely2,
	GreenSidely,
	RedSidely,
	SidelyBlack,
	SurfaceColor,
	GreySidely
} from '../../styles/global/css/Utils';
import { OrangeSidely } from '../../styles/global/css/Utils';
import { Translate, translateToString } from '../../styles/global/translate';
import { CreationPopupContainer, creationPopupDropdownStyle, creationPopupInputStyle } from '../../components_v2/popup/style/CreationFormStyle';
import { FlexDiv } from '../products/style';
import { creationPopupInputStyleMargin } from '../promotions/PopupPromotion';
import { PinStyle } from '../settings/subSettings/actions';
import { getField, postField, putField } from './actions';
import { Field } from '../../atoms/forms';
import { ModalData } from './FormTemplateCreator';
import { DefaultButton } from '../../styles/global/css/GlobalButton';

export const ALL_FIELD_TYPES: FieldType[] = ['Text', 'Number', 'Date', 'Boolean', 'Select', 'Multiselect', 'File', 'Image', 'Url', 'Email', 'Phone', 'Address', 'Location', 'User', 'Company', 'Integer', 'CheckIn', 'Form', 'TextArea', 'Signature', 'Money'];

type MetaData = {
	Select?: string[],
	Multiselect?: string[],
	Form?: number,
	CheckIn?: number,
	Image?: { gallery?: boolean, camera?: boolean },
}

const SelectMetaDataContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const EditWarningBanner = styled.div`
	background-color: color-mix(in oklab, ${OrangeSidely}, ${SurfaceColor} 80%);
	border: 1px solid color-mix(in oklab, ${OrangeSidely}, ${SurfaceColor} 50%);
	border-radius: 5px;
	padding: 8px 12px;
	font-size: 12px;
	color: color-mix(in oklab, ${OrangeSidely}, ${SidelyBlack} 40%);
`;

export const SelectIconContainer = styled.div<{
	iconSelected?: boolean,
	color?: string,
	size?: number | string,
	position?: string,
	left?: string,
	top?: string,
	objectFit?: string,
	pointerEvents?: string
}>`
	background-color: ${SurfaceColor};
	display: flex;
	justify-content: center;
	align-items: center;
	width: ${p => p.size ? (typeof p.size === 'number' ? `${p.size}px` : p.size) : '24px'};
	aspect-ratio: 1;
	border-radius: 50%;
	${p => p.iconSelected ? '' : p.color ? `border: 1px solid ${p.color}` : `border: 1px solid ${PrimaryColor}`}
	${p => p.position ? `position: ${p.position}` : ''}
	${p => p.left ? `left: ${p.left}` : ''}
	${p => p.top ? `top: ${p.top}` : ''}
	${p => p.objectFit ? `object-fit: ${p.objectFit}` : ''}
	${p => p.pointerEvents ? `pointer-events: ${p.pointerEvents}` : ''}
`;

export function SelectMetaData<IsColor extends boolean = false>(props: {
	title?: string,
	value?: string,
	first: boolean,
	locked?: boolean,
	onDelete?: () => void,
	onChange: (value: IsColor extends false ? string : { data?: string, pinStyle?: PinStyle }) => void,
	color?: IsColor
	selectedPinStyle?: PinStyle,
}) {
	type OnChangeType = IsColor extends false ? string : { data?: string, pinStyle?: PinStyle };
	return <SelectMetaDataContainer>
		{props.color && <FlexDiv gap='6px'>
			<FlexDiv justify='center' style={{ marginTop: props.first ? '21px' : undefined }}>
				<DropdownIcons
					dropdownStyle={{ containerHeight: 'fit-content' }}
					name='iconPicker'
					autoOptionUp
					dotSize='24px'
					optionDotSize={24}
					selectedValue={props.selectedPinStyle?.logo}
					setPinStyleLogo={(value) => props.onChange({ pinStyle: { logo: value ?? undefined } } as OnChangeType)}
					onChange={(value) => props.onChange({ pinStyle: { logo: value.value } } as OnChangeType)}
					iconColor={props.selectedPinStyle?.color ?? getUserColor(props.value ?? 'MetaData')}
				/>
			</FlexDiv>
			<div style={{ marginTop: props.first ? '21px' : undefined }}>
				<DropdownColor
					dropdownStyle={{ containerHeight: 'fit-content' }}
					name='colorPicker'
					autoOptionUp
					dotSize='24px'
					optionDotSize={24}
					optionPadding={'0'}
					selectedValue={props.selectedPinStyle?.color ?? getUserColor(props.value ?? 'MetaData')}
					onChange={(value) => props.onChange({ pinStyle: { color: value.value } } as OnChangeType)}
				/>
			</div>
		</FlexDiv>}
		<Input
			disabled={!!props.locked}
			required
			label={props.first ? (props.title ?? 'MetaData') : undefined}
			value={props.value}
			type='text'
			name='selectMetaData'
			inputStyle={props.first ? creationPopupInputStyleMargin : creationPopupInputStyle}
			onChange={(value) => props.color ? props.onChange({ data: value } as OnChangeType) : props.onChange(value as OnChangeType)}
		/>
		{props.onDelete !== undefined && !props.locked && <div onClick={props.onDelete}><CloseIcon color={DarkGreySidely2} size={24} style={{ marginTop: props.first ? '21px' : undefined, flexShrink: 0, cursor: 'pointer' }} /></div>}
	</SelectMetaDataContainer>;
}

const metaDataValidator = (metaData: MetaData, type: FieldType) => {
	switch (type) {
		case 'Select':
		case 'Multiselect':

			return metaData[type] !== undefined && metaData[type]!.length !== 0 && metaData[type]!.every(e => e.length);
		case 'Form':
		case 'CheckIn':

			return metaData[type] !== undefined && metaData[type]! > 0;
		default: return true;
	}
};

type MultiDimensionFieldData = {
	is_company: boolean,
	is_form: boolean,
	is_additional: boolean,
	is_product: boolean,
	is_promotion: boolean,
}

export const BoolCheckbox = styled.input`
	margin: 0;
	accent-color: ${PrimaryColor};
	cursor: pointer;
`;

type YesNoCheckBoxProps = {
	checked: boolean,
	onChange: (checked: boolean) => void,
	stacked?: boolean
	nullable?: false
} | {
	checked?: boolean,
	onChange: (checked: boolean | undefined) => void,
	stacked?: boolean,
	nullable: true
}

export function YesNoCheckbox(props: YesNoCheckBoxProps) {
	if (!props.stacked) {
		const color = props.checked ? GreenSidely : props.checked === false ? RedSidely : GreySidely;
		return <DefaultButton margin='0' style={{ backgroundColor: `${color}15`, color }} onClick={() => {
			if (props.nullable === true) {
				props.onChange(props.checked === true ? undefined : (props.checked === false ? true : false));
			} else {
				props.onChange(!props.checked);
			}
		}}><Translate id={props.checked ? 'global.yes' : props.checked === false ? 'global.no' : 'none'} /></DefaultButton>;
	}
	return <FlexDiv justify='space-evenly' width='100%' gap='5px'>
		<DefaultButton margin='0' buttonStyle={props.checked ? undefined : ButtonStyle.White} color={GreenSidely} style={{ backgroundColor: props.checked ? GreenSidely : undefined, width: '50%' }} onClick={() => {
			if (props.nullable === true) {
				props.onChange(props.checked === true ? undefined : true);
			} else {
				props.onChange(true);
			}
		}}><Translate id='global.yes' /></DefaultButton>
		<DefaultButton margin='0' buttonStyle={props.checked ? ButtonStyle.White : undefined} color={RedSidely} style={{ backgroundColor: props.checked ? undefined : RedSidely, width: '50%' }} onClick={() => {
			if (props.nullable === true) {
				props.onChange(props.checked === false ? undefined : false);
			} else {
				props.onChange(false);
			}
		}}><Translate id='global.no' /></DefaultButton>
	</FlexDiv>;

}

export default function PopupField(props: { isOpen: boolean, setIsOpen: (b: boolean) => void, field: ModalData, onCreate: (field?: Field) => void, notAdvancedMode?: boolean }) {
	const [type, setType] = React.useState<FieldType | undefined>(props.field.type);
	const [name, setName] = React.useState<string>(props.field.name ?? '');
	const [multidimensionBools, setMultidimensionBools] = React.useState<MultiDimensionFieldData>({ is_company: props.field.is_company ?? false, is_form: props.field.is_form ?? false, is_additional: props.field.is_additional ?? false, is_product: props.field.is_product ?? false, is_promotion: props.field.is_promotion ?? false });
	const [data, setData] = React.useState<MetaData>({});
	// a promotion field computed by the backend: its formula, how it shows
	const [formula, setFormula] = React.useState<string>('');
	const [format, setFormat] = React.useState<string>('');
	const [formulaError, setFormulaError] = React.useState<string | undefined>(undefined);
	// the colours of the values in the promotion lists
	const [displayOptions, setDisplayOptions] = React.useState<ColumnDisplayOptions | undefined>(undefined);
	const [colorsOpen, setColorsOpen] = React.useState(false);
	// the guide of the formula or the format, opened from the "?" of its label
	const [guide, setGuide] = React.useState<GuideKind>();
	const textAreaRef = React.useRef<HTMLTextAreaElement>(null);
	const constraintEditor = React.useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
	const [constraint, setConstraint] = React.useState<string | undefined>(props.field.constraint ? JSON.stringify(props.field.constraint) : '{}');
	// Custom calendar display of a Date field, stored inside the field's data
	// json (the per-type config column) — see get_instances_within_two_dates.
	// visit_label is a display name: it pairs a start with its end when a
	// visit is split into chunks and captions the calendar block; empty means
	// the main block and is omitted from the json
	const [visitBound, setVisitBound] = React.useState<'start' | 'end' | undefined>(undefined);
	const [visitLabel, setVisitLabel] = React.useState<string>('');
	// block color on the calendar, configured on the start field only
	const [visitColor, setVisitColor] = React.useState<string | undefined>(undefined);
	const isDarkMode = getLocalStorage('isDarkMode') ?? false;

	const onMount = React.useCallback((editor) => {
		constraintEditor.current = editor;
		setTimeout(() => {
			editor.getAction('editor.action.formatDocument')?.run();
		}, 200);
		setTimeout(() => {
			editor.revealLine(1);
		}, 300);
		editor.getAction('editor.action.formatDocument')?.run();
	}, []);

	React.useEffect(() => setType(props.field.type), [props.field.type]);
	React.useEffect(() => setName(props.field.name ?? ''), [props.field.name]);
	const reset = () => {
		setType(undefined);
		setName('');
		setData({});
		setFormula('');
		setFormat('');
		setDisplayOptions(undefined);
		setFormulaError(undefined);
	};

	React.useEffect(() => {
		if (props.field.field_id !== undefined && props.isOpen) {
			getField(props.field.field_id).then(field => {
				setType(field.type);
				setName(field.name);
				setMultidimensionBools({ is_company: field.is_company, is_form: field.is_form, is_additional: field.is_additional, is_product: field.is_product, is_promotion: field.is_promotion });
				setData(field.data ? { [field.type]: field.data } : {});
				setConstraint(field.constraint ? JSON.stringify(field.constraint) : '{}');
				setFormula(field.formula ?? '');
				setFormat(field.format ?? '');
				setDisplayOptions(field.display_options ?? undefined);
				const dateData = field.type === 'Date' && field.data && !Array.isArray(field.data) ? field.data as { visit_bound?: 'start' | 'end', visit_label?: string, visit_color?: string } : undefined;
				setVisitBound(dateData?.visit_bound ?? undefined);
				setVisitLabel(typeof dateData?.visit_label === 'string' ? dateData.visit_label : '');
				setVisitColor(typeof dateData?.visit_color === 'string' ? dateData.visit_color : undefined);
			});
		}
	}, [props.field.field_id, props.isOpen]);

	return <Popup
		popupMode={PopupMode.Details}
		isOpen={props.isOpen}
		onClickOut={() => {
			reset();
			props.setIsOpen(false);
		}}
	>
		<PopupCreation
			messageButton={props.field.field_id ? 'Sauvegarder' : 'Créer'}
			title={translateToString(props.field.field_id ? 'form_editor.field_edition_title' : 'form_editor.field_creation_title')}
			canValidate={type !== undefined && name.length !== 0 && metaDataValidator(data, type)}
			onSubmit={() => {
				if (props.field.field_id) {
					if (type) {
						putField(props.field.field_id, {
							id: props.field.field_id,
							type,
							name,
							data: type === 'Date' ? (visitBound ? { visit_bound: visitBound, ...(visitLabel.trim() ? { visit_label: visitLabel.trim() } : {}), ...(visitBound === 'start' && visitColor ? { visit_color: visitColor } : {}) } : undefined) : data[type],
							is_company: multidimensionBools.is_company,
							is_form: multidimensionBools.is_form,
							is_additional: multidimensionBools.is_additional,
							is_product: multidimensionBools.is_product,
							constraint: constraint === '{}' || !constraint ? undefined : JSON.parse(constraint),
							is_promotion: multidimensionBools.is_promotion,
							formula: formula.trim() || undefined,
							format: format.trim() || undefined,
							display_options: hasColorOptions(displayOptions) ? displayOptions : undefined,
						}).then(() => {
							reset();
							props.onCreate();
						}).catch(e => setFormulaError(String(e?.response?.data ?? e)));
					}
				} else {
					if (!type) return;
					postField({
						type,
						name,
						data: type === 'Date' ? (visitBound ? { visit_bound: visitBound, ...(visitLabel.trim() ? { visit_label: visitLabel.trim() } : {}), ...(visitBound === 'start' && visitColor ? { visit_color: visitColor } : {}) } : undefined) : data[type],
						is_company: multidimensionBools.is_company,
						is_form: multidimensionBools.is_form,
						is_additional: multidimensionBools.is_additional,
						is_product: multidimensionBools.is_product,
						constraint: constraint === '{}' || !constraint ? undefined : JSON.parse(constraint),
						is_promotion: multidimensionBools.is_promotion,
						formula: formula.trim() || undefined,
						format: format.trim() || undefined,
						display_options: hasColorOptions(displayOptions) ? displayOptions : undefined,
					}).then((field) => {
						reset();
						props.onCreate(field);
					}).catch(e => setFormulaError(String(e?.response?.data ?? e)));
				}

			}}
			onClose={() => props.setIsOpen(false)}
		>
			<CreationPopupContainer gap='1em'>
				{props.field.field_id !== undefined && <EditWarningBanner>{translateToString('form_editor.field_edit_warning')}</EditWarningBanner>}
				{!props.notAdvancedMode && <div>slug: {props.field.slug}</div>}
				<Dropdown
					dropdownStyle={{ ...creationPopupDropdownStyle, margin: '11px 0' }}
					datalist={ALL_FIELD_TYPES.map(value => ({ label: value, value }))}
					name='type-dropdown'
					onChange={value => setType(value.value)}
					disabled={props.field.field_id !== undefined}
					selectedValue={type ? { label: type, value: type } : undefined}
					required
					label={translateToString('type')}
				/>
				{type && <Input type='text' name='title-input' value={name} onChange={setName} inputStyle={creationPopupInputStyleMargin} required label={translateToString('title')} />}
				{type === 'Date' && <Dropdown<'start' | 'end' | undefined>
					cancellable
					dropdownStyle={{ ...creationPopupDropdownStyle, margin: '11px 0' }}
					name='visit-bound-dropdown'
					label={translateToString('form_editor.calendar_display.label')}
					datalist={[
						{ label: translateToString('form_editor.calendar_display.start'), value: 'start' },
						{ label: translateToString('form_editor.calendar_display.end'), value: 'end' },
					]}
					selectedValue={visitBound ? { value: visitBound, label: translateToString(`form_editor.calendar_display.${visitBound}`) } : undefined}
					onChange={value => setVisitBound(value.value ?? undefined)}
				/>}
				{type === 'Date' && visitBound && <Input
					type='text'
					name='visit-label-input'
					value={visitLabel}
					onChange={setVisitLabel}
					inputStyle={creationPopupInputStyleMargin}
					label={translateToString('form_editor.calendar_display.name')}
				/>}
				{type === 'Date' && visitBound === 'start' && <FlexDiv gap='10px' align='center'>
					<DropdownColor
						dropdownStyle={{ containerHeight: 'fit-content' }}
						name='visit-color-picker'
						autoOptionUp
						dotSize='24px'
						optionDotSize={24}
						optionPadding='0'
						selectedValue={visitColor}
						onChange={value => setVisitColor(value.value)}
					/>
					<span style={{ fontSize: '12px', color: DarkGreySidely2 }}>{translateToString('form_editor.calendar_display.color')}</span>
				</FlexDiv>}
				{(type === 'Select' || type === 'Multiselect') && <>
					<label style={{
						fontSize: '12px',
						color: DarkGreySidely2,
					}}>{translateToString('form_editor.modify_multiple_values')}
						<textarea ref={textAreaRef} defaultValue={data[type]?.map(e => `${e}`).join('\n')}
							style={{
								width: '100%',
								border: `1px solid ${BorderColor}`,
								borderRadius: '5px',
								fontSize: '12px',
								padding: '9px 12px',
								color: DarkGreySidely2,
							}}
							onChange={e => {
								if (textAreaRef.current) {
									textAreaRef.current.style.height = '';
									textAreaRef.current.style.height = textAreaRef.current.scrollHeight + 3 + 'px';
								}
								setData(md => {
									md[type] = e.target.value.split('\n').map(e => e.trim()).filter(e => e.length > 0);
									return { ...md };
								});
							}} />
					</label>
					{data[type]?.map((e, i) => <SelectMetaData
						value={e}
						key={`selectedMetaData[${i}]`}
						first={i === 0}
						onDelete={() => {
							setData(md => {
								md[type]?.splice(i, 1);
								return { ...md };
							});
						}}
						onChange={value => {
							setData(md => {
								if (!md[type]) md[type] = [];

								md[type]![i] = value;
								return { ...md };
							});
						}}
					/>) ?? <></>}
					<Add onClick={() => setData(md => {
						if (!md[type]) md[type] = [];
						md[type]?.push('');
						return { ...md };
					})} />
				</>}
				{(type === 'Form' || type === 'CheckIn') && <>
					<Input type='number' name='form-id' value={props.field.field_id ? data[type] : 0} onChange={value => {
						setData(md => {
							md[type] = parseInt(value);
							return { ...md };
						});
					}} inputStyle={creationPopupInputStyleMargin} required label={translateToString('form-id')} />
				</>}
				{type === 'Image' &&
					<FlexDiv fontSize='13px' gap='10px' cursor='pointer'
						onClick={() => setData(md => ({ ...md, Image: { ...md.Image, gallery: !(md.Image?.gallery ?? true) } }))}>
						<BoolCheckbox type='checkbox' checked={!(data.Image?.gallery ?? true)} onChange={() => undefined} />
						{translateToString('form_editor.image.restrict_gallery')}
					</FlexDiv>
				}
				{!props.notAdvancedMode &&
					<FlexDiv flow='column' align='stretch' gap='6px'>
						<label style={{
							fontSize: '12px',
							color: DarkGreySidely2,
							marginBottom: '0px',
						}}>
							{translateToString('form_editor.field_domain')}
						</label>
						{Object.keys(multidimensionBools).map((key, i) =>
							<FlexDiv key={`${key}${i}`} fontSize='13px' color='' gap='10px' cursor='pointer'
								onClick={() => setMultidimensionBools({ ...multidimensionBools, [key]: !multidimensionBools[key] })}>
								<BoolCheckbox
									type='checkbox'
									checked={multidimensionBools[key]} />
								{translateToString(`form_editor.domain_${key}`)}
							</FlexDiv>
						)
						}
					</FlexDiv>
				}
				{!props.notAdvancedMode && multidimensionBools.is_promotion &&
					<FlexDiv flow='column' align='stretch' gap='6px'>
						<Input
							type='text'
							name='formula'
							textArea
							value={formula}
							onChange={value => { setFormula(value); setFormulaError(undefined); }}
							placeholder={translateToString('form_editor.formula_placeholder')}
							label={<LabelWithGuide label={translateToString('form_editor.formula')} onGuide={() => setGuide('formula')} />}
							inputStyle={{ ...creationPopupInputStyleMargin, height: '70px' }}
						/>
						<span style={{ fontSize: '11px', color: DarkGreySidely2 }}>{translateToString('form_editor.formula_hint')}</span>
						{formulaError && <span style={{ fontSize: '12px', color: RedSidely }}>{formulaError}</span>}
						<Input
							type='text'
							name='format'
							value={format}
							onChange={setFormat}
							placeholder={translateToString('form_editor.format_placeholder')}
							label={<LabelWithGuide label={translateToString('form_editor.format')} onGuide={() => setGuide('format')} />}
							inputStyle={creationPopupInputStyleMargin}
						/>
						<span style={{ fontSize: '11px', color: DarkGreySidely2 }}>{translateToString('form_editor.format_hint')}</span>
						{type && mapColorsKindOf(type) && <FlexDiv gap='8px' align='center' cursor='pointer' fontSize='13px' onClick={() => setColorsOpen(true)}>
							<Palette size={16} />
							{translateToString('color_scale.values')}
							{hasColorOptions(displayOptions) && <span style={{ color: DarkGreySidely2, fontSize: '12px' }}>({translateToString('color_scale.set')})</span>}
						</FlexDiv>}
						<FieldGuidePopup kind={guide} onClose={() => setGuide(undefined)} />
						<MapColorsPopup
							isOpen={colorsOpen}
							title={name || undefined}
							kind={type ? mapColorsKindOf(type) : undefined}
							date={type === 'Date'}
							options={displayOptions}
							onClose={() => setColorsOpen(false)}
							onSave={setDisplayOptions}
						/>
					</FlexDiv>
				}
				{!props.notAdvancedMode &&
					<FlexDiv flow='column' align='stretch' gap='6px' height='100%'>
						<label style={{
							fontSize: '12px',
							color: DarkGreySidely2,
							marginBottom: '0px',
						}}>
							{translateToString('form_editor.constraint')}
						</label>
						<Editor
							height='200px'
							language='json'
							value={constraint}
							theme={isDarkMode ? 'vs-dark' : 'light'}
							saveViewState={false}
							onChange={(value) => {
								try {
									setConstraint(value);
								} catch (_e) {
									console.error('Invalid JSON');
								}
							}}
							options={{
								minimap: { enabled: false },
								folding: false,
								glyphMargin: false,
							}}
							onMount={onMount}
						/>
					</FlexDiv>
				}
			</CreationPopupContainer>

		</PopupCreation>
	</Popup>;
}
